import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { IconName } from '@app/utils/enums/icon-enum';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { FeedbackService } from '@app/services/feedback/feedback.service';
import { finalize, merge, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { SnackbarService } from '@app/services/snackbar/snackbar.service';
import { SnackbarStatus } from '@app/utils/enums/snackbar.enum';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CreateFeedbackPayload } from '@app/utils/interfaces/system-feedback.interface';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-feedback-modal',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogModule,
    IconComponent,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
  templateUrl: './feedback-modal.component.html',
  styleUrl: './feedback-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackModalComponent {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<FeedbackModalComponent>);
  private readonly feedbackService = inject(FeedbackService);
  private readonly snackbarService = inject(SnackbarService);

  public readonly IconName = IconName;

  public readonly maxRating = 5;
  public readonly maxTitleLength = 60;
  public readonly maxTextLength = 500;
  public readonly stars = [1, 2, 3, 4, 5] as const;

  public readonly hoveredRating = signal(0);
  public readonly selectedRating = signal(0);
  public readonly loading = signal(false);
  public readonly ratingTouched = signal(false);

  public readonly filledStars = computed<boolean[]>(() => {
    const activeRating = this.hoveredRating() || this.selectedRating();

    return this.stars.map((star) => star <= activeRating);
  });

  public readonly ratingError = computed(() => this.ratingTouched() && !this.selectedRating());

  private readonly allowedInputPattern = /^[A-Za-z0-9À-ž\s'",.!?;:()&#@%+/_-]*$/;

  public readonly form = this.fb.nonNullable.group({
    title: [
      '',
      [
        Validators.maxLength(this.maxTitleLength),
        this.allowedCharactersValidator(this.allowedInputPattern),
      ],
    ],
    text: [
      '',
      [
        Validators.maxLength(this.maxTextLength),
        this.allowedCharactersValidator(this.allowedInputPattern),
      ],
    ],
    isPublic: [false],
  });

  public readonly titleError$ = this.createControlErrorStream(this.form.controls.title);
  public readonly textError$ = this.createControlErrorStream(this.form.controls.text);

  public setHoveredRating(rating: number): void {
    this.hoveredRating.set(rating);
  }

  public clearHoveredRating(): void {
    this.hoveredRating.set(0);
  }

  public selectRating(rating: number): void {
    this.ratingTouched.set(true);
    this.selectedRating.set(rating);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSubmit(): void {
    this.ratingTouched.set(true);
    this.form.markAllAsTouched();

    if (!this.selectedRating() || this.form.invalid) {
      return;
    }

    const raw = this.form.getRawValue();

    const payload: CreateFeedbackPayload = {
      rating: this.selectedRating(),
      title: raw.title.trim(),
      text: raw.text.trim(),
      isPublic: raw.isPublic,
    };

    this.loading.set(true);

    this.feedbackService
      .createFeedback(payload)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackbar(SnackbarStatus.SUCCESS);
          this.dialogRef.close(true);
        },
        error: () => {
          this.snackbarService.openSnackbar(SnackbarStatus.FAIL);
          this.dialogRef.close(false);
        },
      });
  }

  private createControlErrorStream(control: AbstractControl<string>): Observable<string> {
    return merge(control.valueChanges, control.statusChanges).pipe(
      startWith(null),
      map(() => this.getControlErrorMessage(control)),
    );
  }

  private getControlErrorMessage(control: AbstractControl<string>): string {
    if (!control.touched || !control.errors) {
      return '';
    }

    if (control.hasError('maxlength')) {
      const requiredLength = control.getError('maxlength')?.requiredLength;
      return `Maximum length is ${requiredLength} characters.`;
    }

    if (control.hasError('invalidCharacters')) {
      return 'Invalid input. Please use only allowed characters.';
    }

    return '';
  }

  private allowedCharactersValidator(pattern: RegExp): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as string | null | undefined;

      if (!value) {
        return null;
      }

      return pattern.test(value) ? null : { invalidCharacters: true };
    };
  }
}
